import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchDevice} from '@/entities/device/api';
import {
  deviceTypeIcon,
  deviceTypeLabelKey,
  isTerminalLike,
  type DeviceDetail,
  managingDesktop,
} from '@/entities/device/types';
import {GateModeSwitch} from '@/features/view-devices/ui/GateModeSwitch';
import {ManagedByDesktopBanner} from '@/features/view-devices/ui/ManagedByDesktopBanner';
import {TerminalLiveVideo} from '@/features/view-live-video/ui/TerminalLiveVideo';
import {Badge} from '@/shared/ui/badge';
import {AppButton} from '@/shared/ui/button';
import {Card, IconBubble} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Field, FieldGroup} from '@/shared/ui/field';
import {Icon} from '@/shared/ui/icons';
import {Skeleton} from '@/shared/ui/skeleton';
import {StatusPill, type StatusTone} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';
import {formatTimestamp} from '@/shared/lib/format';

/**
 * One terminal, with the only thing the Home context can change about it — its gate
 * mode. Online is teal; offline is muted grey (`neutral`), never the red destructive
 * tone (that means a denied pass, not a sleeping camera).
 *
 * The head states the device the way the list does (glyph in a state-tinted bubble),
 * so the transition from row to card is continuous; the gate switch is lifted out of
 * the facts list into its own card, because it is the one thing here that WRITES.
 */
/**
 * Kilobytes → "138 МБ". Absence is a DASH, never a zero — see `DeviceSummary`.
 * `Field` draws the dash itself when the value is null, so that is what we hand it.
 */
function formatMemory(kb?: number | null): string | null {
  return kb == null ? null : `${Math.round(kb / 1024)} MB`;
}

/** Bytes → "8.2 МБ". Absence is a dash. */
function formatBytes(bytes?: number | null): string | null {
  if (bytes == null) {
    return null;
  }
  return bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Under 40 MB of `MemAvailable` is worth saying out loud.
 *
 * Silence is NOT alarming: an unknown is not a problem, and colouring it as one would
 * light up every board still on old firmware the day this shipped. Same thresholds the
 * admin console uses, so the two products do not disagree about the same number.
 */
function lowMemory(kb?: number | null): boolean {
  return kb != null && kb < 40 * 1024;
}

/** The log lives in tmpfs, capped at 8 MB on the board. 16 means trimming is broken. */
function oversizedLog(bytes?: number | null): boolean {
  return bytes != null && bytes > 16 * 1024 * 1024;
}

export type DeviceCardProps = {
  companyId: string;
  deviceId: string;
  /** Opens the terminal-settings screen (profiles, config, live status, control). */
  onOpenSettings?: () => void;
};

/** Online → live teal; offline → muted grey, deliberately not red. */
function deviceTone(online: boolean): StatusTone {
  return online ? 'online' : 'neutral';
}

const styles = StyleSheet.create({
  root: {gap: spacing(4)},
  head: {flexDirection: 'row', alignItems: 'center', gap: spacing(3), paddingVertical: spacing(2)},
  headText: {flex: 1, gap: spacing(2)},
  pills: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), flexWrap: 'wrap'},
});

export function DeviceCard({companyId, deviceId, onOpenSettings}: DeviceCardProps) {
  const {t} = useTranslation();
  const p = useTheme();

  const query = useQuery({
    queryKey: ['device', companyId, deviceId],
    queryFn: () => fetchDevice(companyId, deviceId),
  });

  if (query.isPending) {
    return (
      <View style={styles.root}>
        <Skeleton height={64} radius={radii.lg} />
        <Skeleton height={96} radius={radii.lg} />
        <Skeleton height={230} radius={radii.lg} />
      </View>
    );
  }

  if (query.isError) {
    return (
      <EmptyState
        tone="error"
        title={t('home.device.notFound')}
        actionLabel={t('actions.retry')}
        onAction={() => {
          void query.refetch();
        }}
      />
    );
  }

  const detail: DeviceDetail = query.data;
  const s = detail.summary;
  const pendingSync =
    s.pending_settings_sync || s.pending_personnel_sync || s.pending_full_sync;
  // A camera has no door and a desktop bridge is not standing at one: neither the
  // gate direction nor the terminal-settings screen means anything for them, and
  // offering either would be an invitation to write a setting nothing reads.
  const terminal = isTerminalLike(s.device_type);
  // A terminal behind a desktop takes its gate mode from that desktop: the switch
  // here would be a write the server refuses (409).
  const manager = managingDesktop(s);

  return (
    <View style={styles.root}>
      <View style={styles.head}>
        <IconBubble tone={s.online ? p.signalSoft : p.muted} size={52} square>
          <Icon
            name={deviceTypeIcon(s.device_type)}
            size={26}
            color={s.online ? p.signal : p.mutedForeground}
          />
        </IconBubble>
        <View style={styles.headText}>
          <AppText variant="title" numberOfLines={2}>
            {s.device_name}
          </AppText>
          {s.machine_number ? (
            <AppText variant="mono" color={p.mutedForeground} selectable>
              {s.machine_number}
            </AppText>
          ) : null}
          <View style={styles.pills}>
            <StatusPill
              tone={deviceTone(s.online)}
              size="sm"
              label={t(s.online ? 'home.devices.online' : 'home.devices.offline')}
            />
            {/* What KIND of terminal this is — a label, not a verdict. A dotless
                `StatusPill` was standing in for a badge here, which meant a device's
                type sat in the status family beside its actual state. */}
            <Badge variant="muted" size="sm" label={t(deviceTypeLabelKey(s.device_type))} />
            {pendingSync ? (
              <StatusPill
                tone="pending"
                size="sm"
                dot={false}
                label={t('home.device.pendingSync')}
              />
            ) : null}
          </View>
        </View>
      </View>

      {manager ? <ManagedByDesktopBanner manager={manager} /> : null}

      {terminal && !manager ? (
        <Card padding={4}>
          <GateModeSwitch
            companyId={companyId}
            deviceId={deviceId}
            current={s.gate_mode}
          />
        </Card>
      ) : null}

      {/* EXPERIMENT: live video from the terminal's own camera. */}
      {terminal ? (
        <TerminalLiveVideo
          companyId={companyId}
          deviceId={deviceId}
          machineNumber={s.machine_number}
          deviceName={s.device_name}
        />
      ) : null}

      <FieldGroup>
        <Field label={t('home.device.ip')} value={s.ip_address} mono copyable />
        <Field label={t('home.device.ipv6')} value={s.ipv6_address} mono copyable />
        <Field label={t('home.device.mac')} value={s.mac_address} mono copyable />
        <Field
          label={t('home.device.machineNumber')}
          value={s.machine_number}
          mono
          copyable
        />
        <Field label={t('home.device.appVersion')} value={s.app_version} mono />
        {terminal ? (
          <Field
            label={t('home.device.firmwareVersion')}
            value={s.firmware_version}
            mono
          />
        ) : null}
        <Field
          label={t('home.device.personnel')}
          value={t('home.devices.personnelCount', {count: s.personnel_count})}
        />
        <Field
          label={t('home.device.lastSeen')}
          value={formatTimestamp(s.last_seen_at)}
          mono
        />
        <Field
          label={t('home.device.lastSync')}
          value={formatTimestamp(s.last_sync_at)}
          mono
        />
      </FieldGroup>

      {/* What the board has left of itself. Its own group, because these three are
          about the hardware rather than about the deployment — and because a terminal
          on firmware older than 2026-08-20 reports none of them, in which case the
          whole group is dashes and saying so once, at the top, beats three mysteries. */}
      {terminal ? (
        <FieldGroup>
          <Field
            label={t('home.device.availableMemory')}
            value={formatMemory(s.available_memory_kb)}
            mono
            trailing={
              lowMemory(s.available_memory_kb) ? (
                <Badge variant="warn" size="sm" label={t('home.device.low')} />
              ) : undefined
            }
          />
          <Field
            label={t('home.device.logSize')}
            value={formatBytes(s.log_bytes)}
            mono
            note={t('home.device.logHint')}
            trailing={
              oversizedLog(s.log_bytes) ? (
                <Badge variant="warn" size="sm" label={t('home.device.notTrimming')} />
              ) : undefined
            }
          />
          <Field
            label={t('home.device.dbSize')}
            value={formatBytes(s.db_bytes)}
            mono
            note={t('home.device.dbHint')}
          />
        </FieldGroup>
      ) : null}

      {onOpenSettings && terminal ? (
        <AppButton
          title={t('terminal.open')}
          tone="neutral"
          icon="settings"
          onPress={onOpenSettings}
        />
      ) : null}
    </View>
  );
}
